/**
 * Common Translations Module (Japanese)
 * 
 * Contains: Navigation, Footer, Settings, UI Elements, Tooltips, Common Utilities
 * 
 * @module common
 * @language ja
 */

export const common = {
  nav: {
    // 1. Overview (standalone)
    overview: "概要",
    
    // 2. Youth Futures (dropdown parent)
    youthFutures: "ユースフューチャーズ",
    // Youth Futures dropdown items
    impactMetrics: "インパクトと指標",
    mentalHealth: "メンタルヘルス次元",
    workforceEducation: "労働力と教育次元",
    entrepreneurshipSupport: "起業支援次元",
    
    // 3. Database (dropdown parent)
    database: "データベース",
    // Database dropdown items
    memberInsights: "会員インサイト",
    programsEvents: "プログラムとイベント",
    technologyFuture: "テクノロジーと未来",
    regionalAnalysis: "地域分析",
    
    // 4. Vision 2040 (standalone)
    vision2040: "ビジョン2040",
    
    // 5. Know More (dropdown parent)
    knowMore: "詳しく知る",
    // Know More dropdown items
    methodology: "方法論",
    contributing: "貢献",
    faq: "よくある質問",
    
    // Additional nav items
    backToTop: "トップへ戻る",
    brandName: "JCI Vision Pulse",
    observatoryTitle: "ユースフューチャーズ・オブザーバトリー",
    developmentCouncils: "開発評議会"
  },
  footer: {
    copyright: "© 2025 JCI開発評議会。CC BY-NC-SAでライセンス。",
    methodology: "世界中の147,424人のJCI会員のデータに基づく方法論。",
    poweredBy: "提供",
    privacyPolicy: "プライバシーポリシー",
    termsOfService: "利用規約"
  },
  settings: {
    title: "設定",
    subtitle: "好みを管理し、体験をカスタマイズします。",
    appearance: "外観",
    appearanceDescription: "デバイスでのオブザーバトリーの見た目をカスタマイズ。",
    light: "ライト",
    dark: "ダーク",
    system: "システム",
    language: "言語",
    languageDescription: "インターフェースの優先言語を選択。",
    notifications: "通知",
    notificationsDescription: "更新とアラートの受信方法を設定。",
    emailNotifications: "メール通知",
    emailNotificationsDescription: "メールで更新とレポートを受け取る。",
    pushNotifications: "プッシュ通知",
    pushNotificationsDescription: "ブラウザでリアルタイムアラートを受け取る。",
    productUpdates: "製品更新",
    productUpdatesDescription: "新機能と改善について情報を入手。",
    preferencesUpdated: "設定を更新しました",
    notificationsEnabled: "通知が有効になりました",
    notificationsDisabled: "通知が無効になりました",
    themeUpdated: "テーマを更新しました",
    themeChangedTo: "テーマを変更しました",
    languageUpdated: "言語を更新しました",
    languageChangedTo: "言語を変更しました",
    profile: "プロフィール",
    profileDescription: "アカウント情報",
    clickToChangeAvatar: "クリックしてアバターを変更",
    avatar: {
      error: "エラー",
      loginRequired: "アバターをアップロードするにはログインが必要です",
      invalidType: "無効なファイル形式",
      selectImage: "画像ファイルを選択してください",
      tooLarge: "画像が大きすぎます",
      selectSmaller: "より小さい画像を選択してください（圧縮後最大100KB）",
      updated: "アバターを更新しました",
      updatedSuccess: "プロフィール画像が正常に更新されました",
      uploadFailed: "アップロードに失敗しました",
      failedToUpload: "アバターのアップロードに失敗しました"
    },
    observatoryLens: "オブザーバトリーレンズ",
    observatoryLensDescription: "パーソナライズされたデータフォーカス設定",
    primaryInterest: "主な関心",
    focusCountry: "フォーカス国",
    refineLens: "オブザーバトリーレンズを調整",
    translationDebug: "翻訳デバッガー",
    translationDebugDescription: "不足している翻訳キーと完了状況を表示",
    dangerZone: "危険ゾーン",
    dangerZoneDescription: "アカウントに影響する取り消し不可能な操作",
    deleteAccount: "アカウントを削除",
    deleteAccountDescription: "アカウントと関連するすべてのデータを完全に削除します",
    deleteAccountConfirmTitle: "アカウントを削除しますか？",
    deleteAccountConfirmDescription: "この操作は元に戻せません。以下を含むすべてのデータが完全に削除されます：",
    deleteAccountDataProfile: "プロフィール情報",
    deleteAccountDataNotifications: "通知設定と履歴",
    deleteAccountDataAvatar: "プロフィール画像",
    deleteAccountDataProgress: "オブザーバトリーの進捗と実績",
    typeEmailConfirm: "確認のためメールアドレスを入力：",
    deleteAccountButton: "アカウントを完全に削除",
    accountDeleted: "アカウント削除完了",
    accountDeletedDescription: "アカウントと関連するすべてのデータが完全に削除されました。",
    deleteFailed: "削除に失敗しました",
    deleteFailedDescription: "アカウントを削除できませんでした。もう一度お試しいただくか、サポートにお問い合わせください。",
    emailMismatch: "メールアドレスが一致しません",
    // Privacy & Data Export
    privacyData: "プライバシーとデータ",
    privacyDataDescription: "個人データとプライバシー設定を管理",
    downloadData: "データをダウンロード",
    downloadDataDescription: "すべての個人データをJSON形式でエクスポート（プロフィール、通知、設定）",
    downloadAsJson: "JSONでダウンロード",
    downloading: "ダウンロード準備中...",
    downloadComplete: "ダウンロード完了",
    downloadCompleteDescription: "個人データのダウンロードが完了しました。",
    downloadFailed: "ダウンロード失敗",
    downloadFailedDescription: "データをエクスポートできませんでした。もう一度お試しください。",
    cancel: "キャンセル",
    // Data Processing Record - GDPR Article 30
    processingRecord: {
      title: "データ処理記録",
      activitiesRecorded: "件の処理アクティビティが記録されています",
      view: "記録を表示",
      hide: "記録を非表示",
      noActivities: "まだ処理アクティビティが記録されていません",
      legalBasisLabel: "法的根拠",
      showMore: "さらに{{count}}件を表示",
      showLess: "表示を減らす",
      gdprNote: "GDPR第30条に基づき、アカウントに関するすべてのデータ処理活動の記録にアクセスする権利があります。",
      activityTypes: {
        accountCreated: "アカウント作成",
        profileUpdated: "プロフィール情報更新",
        onboardingCompleted: "オンボーディング完了",
        achievementUnlocked: "実績解除",
        preferencesCreated: "通知設定の初期設定",
        preferencesUpdated: "通知設定の更新",
        notificationSent: "通知送信",
        notificationRead: "通知既読",
        roleAssigned: "役割割り当て"
      },
      legalBasis: {
        consent: "同意",
        contractPerformance: "契約の履行",
        legitimateInterest: "正当な利益"
      }
    }
  },
  progress: {
    title: "オブザーバトリーマスタリー",
    description: "オブザーバトリーでの旅を追跡",
    emailVerified: "メール確認済み",
    visitedModules: "訪問済み",
    modules: "モジュール",
    setupComplete: "セットアップ完了",
    observer: "オブザーバー",
    analyst: "アナリスト",
    visionary: "ビジョナリー"
  },
  notifications: {
    title: "通知",
    empty: "通知はまだありません",
    viewSettings: "通知設定",
    markAllRead: "すべて既読にする",
    preferences: "通知設定",
    preferencesDescription: "インテリジェンスブリーフィングの受信方法を管理",
    emailAlerts: "メールアラート",
    emailAlertsDescription: "戦略的インテリジェンスブリーフィングをメールで受信",
    inAppAlerts: "アプリ内アラート",
    inAppAlertsDescription: "ログイン時にトースト通知を表示",
    weeklyDigest: "週間ダイジェスト",
    weeklyDigestDescription: "毎週インテリジェンスの要約を受信",
    intelBriefing: "📊 新しい情報があります",
    deleteAll: "すべて削除",
    deleteAllConfirm: "すべての通知を削除しますか？",
    deleteAllDescription: "すべての通知履歴が完全に削除されます。この操作は元に戻せません。",
    deleteAllButton: "すべての通知を削除",
    deleted: "通知を削除しました",
    deletedDescription: "すべての通知が完全に削除されました。",
    deleteError: "削除に失敗しました",
    deleteErrorDescription: "通知を削除できませんでした。もう一度お試しください。",
    cancel: "キャンセル",
    deletedSingle: "通知を削除しました",
    swipeHint: "左にスワイプして削除"
  },
  roles: {
    admin: "管理者",
    administrator: "管理者",
    moderator: "モデレーター",
    user: "ユーザー",
  },
  interests: {
    "mental health": "メンタルヘルス",
    "mental-health": "メンタルヘルス",
    "workforce preparation": "労働力準備",
    "workforce-preparation": "労働力準備",
    "entrepreneurship": "起業家精神",
    "technology adoption": "技術導入",
    "technology-adoption": "技術導入",
    "regional analysis": "地域分析",
    "regional-analysis": "地域分析",
  },
  mastery: {
    observer: "オブザーバー",
    analyst: "アナリスト",
    visionary: "ビジョナリー",
  },
  visibility: {
    membersOnly: "メンバー専用",
    adminOnly: "管理者専用",
    hiddenAdminOnly: "非表示（管理者のみ）",
  },
  admin: {
    sendBriefing: "インテリジェンスブリーフィングを送信",
    sendBriefingDescription: "購読ユーザーに戦略的インテリジェンスブリーフィングを送信",
    module: "モジュール",
    selectModule: "モジュールを選択",
    briefingTitle: "タイトル",
    titlePlaceholder: "例：新しいメンタルヘルスデータが公開されました",
    summary: "要約",
    summaryPlaceholder: "新しいインテリジェンスの簡単な説明...",
    sendToSubscribers: "購読者に送信",
    recipientNote: "受信者は、メールアラートを有効にしている一致する主な関心を持つユーザーです",
    fillAllFields: "すべてのフィールドを入力してください",
    briefingSent: "ブリーフィング送信完了",
    sentTo: "送信先",
    subscribers: "購読者",
    sendFailed: "ブリーフィングの送信に失敗しました",
    briefingSentTo: "ブリーフィング送信先",
    users: "ユーザー",
    sending: "送信中...",
    // API Key Management
    apiKeys: {
      title: "APIキー",
      description: "YFIデータへの外部ソフトウェアアクセス用APIキーを管理",
      create: "APIキーを作成",
      createTitle: "新しいAPIキーを作成",
      createDescription: "外部ソフトウェアがYFIデータAPIにアクセスするための新しいAPIキーを作成します。",
      name: "名前",
      namePlaceholder: "例：マイアプリケーション",
      keyDescription: "説明",
      descriptionPlaceholder: "このキーは何に使用されますか？",
      expiration: "有効期限",
      never: "無期限",
      days30: "30日",
      days90: "90日",
      months6: "6ヶ月",
      year1: "1年",
      cancel: "キャンセル",
      creating: "作成中...",
      created: "APIキーを作成しました",
      createdDescription: "新しいAPIキーが作成されました。今すぐコピーしてください - 二度と表示されません。",
      saveKeyWarning: "APIキーを今すぐ保存してください！",
      saveKeyDescription: "このキーが表示されるのは今回限りです。コピーして安全に保管してください。",
      savedKey: "キーを保存しました",
      revoke: "取り消し",
      revokeTitle: "APIキーを取り消し",
      revokeDescription: "このキーを取り消してもよろしいですか？この操作は元に戻せません。このキーを使用しているアプリケーションは直ちにアクセスを失います。",
      revoking: "取り消し中...",
      revoked: "APIキーを取り消しました",
      revokedDescription: "APIキーは取り消され、使用できなくなりました。",
      noKeys: "APIキーがまだありません",
      noKeysDescription: "最初のAPIキーを作成して始めましょう。",
      active: "アクティブ",
      expired: "期限切れ",
      created_at: "作成日",
      lastUsed: "最終使用",
      requests: "リクエスト",
      viewDocs: "これらのキーの使用方法についてはAPI統合ガイドを参照してください。",
      failedToLoad: "APIキーの読み込みに失敗しました",
      failedToCreate: "APIキーの作成に失敗しました",
      failedToRevoke: "APIキーの取り消しに失敗しました"
    },
    // Data Administration
    dataAdmin: {
      title: "データ管理",
      description: "オブザーバトリーデータを監視・管理",
      refresh: "更新",
      coverage: "国プロファイルカバレッジ",
      coverageDescription: "の国に詳細プロファイルがあります",
      databaseTables: "データベーステーブル",
      yfiCountries: "YFI国",
      yfiCountriesDesc: "YFIスコアのある国",
      countryProfiles: "国プロファイル",
      countryProfilesDesc: "国別詳細データ",
      jciStats: "JCI統計",
      jciStatsDesc: "グローバル組織メトリクス",
      regions: "リージョン",
      regionsDesc: "JCIリージョン",
      activeApiKeys: "アクティブAPIキー",
      activeApiKeysDesc: "外部ソフトウェアアクセス",
      apiRequests: "APIリクエスト",
      apiRequestsDesc: "記録されたリクエスト総数",
      countryProfilesTitle: "国プロファイル",
      published: "公開済み",
      draft: "下書き",
      updated: "更新日",
      noProfiles: "国プロファイルはまだ作成されていません",
      quickActions: "クイックアクション",
      viewYfiRankings: "YFIランキングを表示",
      regionalAnalysis: "地域分析",
      backendNote: "高度なデータベース管理については、プロジェクト設定からLovable Cloudバックエンドパネルにアクセスしてください。"
    }
  },
  featuredInsight: {
    title: "あなたのフォーカス国",
    fiiScore: "将来インパクト指数",
    globalAverage: "グローバル平均",
    viewFullRankings: "完全なインデックスを見る",
    unlockTitle: "パーソナライズされたインサイトをアンロック",
    unlockDescription: "セットアップを完了して、あなたのフォーカス国がすべてのFII次元でグローバル平均とどのように比較されるかを確認してください。"
  },
  header: {
    title: "JCI開発評議会ユースフューチャーズ・オブザーバトリー",
    subtitle: "開発評議会グローバル・オブザーバトリー",
    tagline: "変化する世界のためのリーダー育成"
  },
  common: {
    jan: "1月",
    feb: "2月",
    mar: "3月",
    apr: "4月",
    may: "5月",
    jun: "6月",
    jul: "7月",
    aug: "8月",
    sep: "9月",
    oct: "10月",
    nov: "11月",
    dec: "12月",
    preferredProgramTypes: "優先プログラムタイプ",
    preferredEventFormat: "優先イベント形式",
    regionalPreferences: "地域の好み",
    inPerson: "対面",
    online: "オンライン",
    hybrid: "ハイブリッド",
    networking: "ネットワーキング",
    development: "開発",
    leadership: "リーダーシップ",
    search: "検索",
    settings: "設定",
    signIn: "ログイン",
    signOut: "ログアウト",
    toggleMenu: "ナビゲーションメニューを切り替え",
    scrollToTop: "トップにスクロール",
    source: "ソース",
    sources: {
      membership2025: "JCI会員2025",
      impactReport2025: "JCIインパクトレポート2025"
    },
    loading: "読み込み中...",
    noDataAvailable: "データがありません",
    noDataDescription: "データが読み込まれているか、現時点で利用できません。",
    errorLoadingChart: "チャートの読み込みエラー",
    months: {
      jan: "1月",
      feb: "2月",
      mar: "3月",
      apr: "4月",
      may: "5月",
      jun: "6月",
      jul: "7月",
      aug: "8月",
      sep: "9月",
      oct: "10月",
      nov: "11月",
      dec: "12月"
    }
  },
  ui: {
    notFound: {
      title: "404",
      message: "おっと！ページが見つかりません",
      returnHome: "ホームに戻る"
    },
    keyboardShortcuts: {
      title: "キーボードショートカット",
      toggleDarkMode: "ダーク/ライトモード切り替え",
      cycleLanguages: "言語を切り替え",
      openSearch: "検索を開く",
      showShortcuts: "ショートカットを表示",
      ctrlOrCmd: "Windows/LinuxではCtrl、MacでAlt+⌘を使用" 
    },
    commandPalette: {
      searchPlaceholder: "ページを検索...",
      noResults: "結果が見つかりません。",
      pagesHeading: "ページ"
    },
    languageToggle: {
      selectLanguage: "言語を選択",
      changeLanguage: "言語を変更"
    }
  },
  charts: {
    noDataAvailable: "データがありません",
    dataLoadingSoon: "データは間もなく読み込まれます",
    noFunnelData: "ファネルデータがありません",
    configureProcessData: "プロセスデータを設定してください",
    radarUnavailable: "レーダーデータが利用できません",
    configurePerformanceIndicators: "パフォーマンス指標を設定してください",
    performanceUnavailable: "パフォーマンスデータが利用できません",
    configureTrendData: "トレンドデータを設定してください",
    currentProgress: "現在の進捗",
    goal2040: "2040年目標",
    mainValue: "メインバリュー",
    trend: "トレンド",
    goal: "目標",
    fiiScore: "FIIスコア",
    score: "スコア",
    value: "値",
    percentage: "パーセント",
    region: "地域",
    country: "国",
    members: "会員",
    year: "年",
    month: "月",
    count: "件数",
    total: "合計",
    current: "現在",
    projected: "予測",
    average: "平均"
  },
  metrics: {
    membersActive: "アクティブ会員",
    countriesPresent: "存在する国",
    startupsSupported: "サポートされたスタートアップ",
    sustainableProjects: "持続可能なプロジェクト",
    completed: "完了",
    countries: "国",
    members: "会員",
    projects: "プロジェクト",
    projectsPerYear: "年間プロジェクト",
    memberDensity: "会員密度",
    country: "国",
    regionalDistribution: "地域別分布",
    globalPresence: "2025年のグローバルJCIプレゼンス",
    activeMembers: "アクティブ会員",
    globally: "世界的に",
    worldwidePresence: "世界的プレゼンス",
    communities: "コミュニティ",
    activeCommunities: "アクティブコミュニティ",
    yearsOfHistory: "歴史の年数",
    since1915: "1915年以来",
    vs2024: "2024年比",
    annualProjects: "年間プロジェクト",
    in2025: "2025年"
  },
  index: {
    heroTitle: "JCI開発評議会ユースフューチャーズ・オブザーバトリー",
    heroSubtitle: "世界規模でユースリーダーシップ、メンタルヘルス、経済機会を測定",
    heroDescription: "114か国の若手リーダーの状況を分析するデータ駆動型プラットフォーム。JCIのグローバルネットワークと研究パートナーシップからの検証済みメトリクスを使用。",
    globalImpactTitle: "グローバルインパクト一覧",
    verifiedData: "JCIインパクトレポート2025からの検証済みデータ",
    globalMembers: "グローバルメンバー",
    activeWorldwide: "世界中でアクティブ",
    projects: "プロジェクト",
    period20242025: "期間: 2024-2025",
    volunteerHours: "ボランティア時間",
    hoursContributed: "貢献時間",
    countries: "国",
    worldwidePresence: "世界的プレゼンス",
    localOrganizations: "ローカル組織",
    activeCommunities: "アクティブコミュニティ",
    digitalReachTitle: "デジタルリーチとフットプリント",
    totalFollowers: "総フォロワー",
    jciccUsers: "jci.ccユーザー",
    memberDemographicsTitle: "会員デモグラフィックスナップショット",
    averageAge: "平均年齢",
    yearsOld: "歳",
    universityEducated: "大学教育",
    bachelorsOrHigher: "学士号以上",
    fullTime: "フルタイム",
    entrepreneurs: "起業家",
    employmentStatus: "雇用状況"
  },
  globalPresenceMap: {
    title: "JCIグローバルプレゼンス",
    subtitle: "4地域の{{count}}カ国でアクティブ",
    regions: "地域",
    countries: "カ国",
    source: "出典：JCIインパクトレポート2025"
  },
  mentalHealth: {
    serviceGapNote: "米国/カナダとラテンアメリカ間で30-40%のサービスカバレッジギャップ",
    criticalGapNote: "重大なギャップ：ヨーロッパの70%以上に対してわずか10-30%のサービスカバレッジ"
  },
  memberInsights: {
    tenureInsight: "53%の新規会員（1-3年）が新鮮なエネルギーをもたらし、24%のベテラン（7年以上）がコアリーダーシップと安定性を提供"
  },
  onboarding: {
    commandCenterSetup: "コマンドセンター設定",
    stepOf: "ステップ {{current}} / {{total}}",
    continue: "続ける",
    back: "戻る",
    completeSetup: "設定完了",
    preferencesUpdated: "設定が更新されました",
    lensUpdated: "オブザーバトリーレンズが更新されました。",
    error: "エラー",
    saveFailed: "設定の保存に失敗しました。もう一度お試しください。",
    identity: {
      title: "身元を確認",
      description: "あなたが誰で、どのJCI組織に所属しているかをお知らせください。",
      fullName: "氏名",
      fullNamePlaceholder: "氏名を入力してください",
      nationalOrg: "JCI全国組織",
      nationalOrgPlaceholder: "全国組織を選択...",
      searchCountry: "国を検索...",
      noCountryFound: "国が見つかりません。",
      localOrg: "JCIローカル組織",
      optional: "任意",
      localOrgPlaceholder: "例：JCI東京、JCI大阪"
    },
    lens: {
      title: "レンズを選択",
      description: "主な関心分野を選択してください。これによりダッシュボードの体験がパーソナライズされます。",
      mentalHealth: "メンタルヘルス",
      mentalHealthDesc: "心理的幸福、レジリエンス、メンタルウェルネスイニシアチブに焦点を当てます。",
      education: "教育と労働力",
      educationDesc: "学習成果、スキル開発、雇用トレンドを探求します。",
      entrepreneurship: "起業家精神",
      entrepreneurshipDesc: "ビジネスエコシステムの健全性、スタートアップ活動、イノベーション指標を発見します。"
    },
    benchmark: {
      title: "ベンチマークを設定",
      description: "ダッシュボール全体でグローバルFII平均と比較するフォーカス国を選択してください。",
      focusCountry: "フォーカス国",
      focusCountryPlaceholder: "フォーカス国を選択...",
      searchCountry: "国を検索...",
      noCountryFound: "国が見つかりません。",
      vsGlobal: "{{country}} vs グローバル平均",
      globalAverage: "グローバル平均",
      aboveAverage: "+{{value}} グローバル平均を上回る",
      belowAverage: "-{{value}} グローバル平均を下回る"
    },
    config: {
      ready: "起動準備完了！",
      configuring: "コマンドセンターを設定中",
      step1: "設定を分析中...",
      step2: "ダッシュボードレイアウトを設定中...",
      step3: "パーソナライズされたインサイトを読み込み中...",
      step4: "コマンドセンターを準備中..."
    }
  },
  fiiClassifications: {
    excellent: "優秀",
    good: "良好",
    moderate: "中程度",
    deficient: "不十分",
    critical: "危機的"
  },
  regions: {
    asiaPacific: "アジアと太平洋",
    europe: "ヨーロッパ",
    americas: "アメリカ",
    africaMiddleEast: "アフリカ・中東"
  },
  access: {
    exclusiveMemberInsight: "会員限定インサイト",
    adminOnlyModule: "管理者専用モジュール",
    joinCTA: "JCI開発評議会ユースフューチャーズ・オブザーバトリーに参加して、完全なデータベース、インサイト、予測をアンロックしてください。",
    adminOnlyMessage: "この{{moduleLabel}}コンテンツは管理者のみが利用できます。",
    joinNowLogin: "今すぐ参加 / ログイン",
    backToDashboard: "ダッシュボードに戻る",
    memberAccess: "メンバーアクセス",
    passwordlessEntry: "Magic LinkまたはGoogleでのパスワードなしエントリー。",
    joinObservatory: "オブザーバトリーに参加",
    unlockDescription: "メンバー限定のインサイト、予測、インタラクティブモジュールをアンロックしてください。",
    email: "メール",
    emailPlaceholder: "you@company.com",
    sendMagicLink: "Magic Linkを送信",
    continueWithGoogle: "Googleで続ける",
    authDisclaimer: "続行すると、認証メールの受信に同意したことになります。",
    signingIn: "サインイン中...",
    checkInbox: "受信トレイを確認してください",
    magicLinkSent: "サインイン用の安全なMagic Linkを送信しました。",
    invalidEmail: "無効なメールアドレス",
    invalidEmailDescription: "有効なメールアドレスを入力してください。",
    loginFailed: "ログインに失敗しました",
    googleLoginFailed: "Googleログインに失敗しました"
  },
  chatJCI: {
    title: "ChatJCI",
    openChat: "チャットを開く",
    closeChat: "チャットを閉じる",
    placeholder: "質問を入力してください...",
    send: "メッセージを送信",
    thinking: "考えています...",
    greeting: "こんにちは、{{name}}さん！今日はどのようにお手伝いできますか？",
    defaultName: "皆さん",
    fallbackMessage: "申し訳ありませんが、その情報はナレッジベースで見つかりませんでした。",
    errorMessage: "申し訳ありませんが、問題が発生しました。もう一度お試しください。",
    rateLimitMessage: "リクエストが多すぎます。しばらくお待ちください。",
    authRequired: "ChatJCIを使用するにはサインインしてください。",
    welcomeSubtitle: "JCIプログラム、会員資格、またはビジョン2040について何でもお聞きください",
    newChat: "新しい会話",
    poweredBy: "Google Gemini搭載",
    suggestedPrompts: {
      title: "こちらについて聞いてみてください：",
      vision2040: "JCIビジョン2040とは何ですか？",
      risePrograms: "JCI RISEプログラムについて教えてください",
      membership: "JCIのメンバーになるにはどうすればいいですか？",
      councils: "開発理事会とは何ですか？",
      youthIndex: "ユースフューチャーズインデックスを説明してください"
    },
    loginRequired: {
      title: "チャットにサインイン",
      description: "ChatJCIはJCIメンバー専用です。サインインして会話を始めましょう。",
      action: "サインイン"
    },
    locked: {
      title: "メンバー専用AIアシスタント",
      description: "ChatJCIは、JCIプログラム、ビジョン2040、ユースフューチャーズインデックスについての個人的なAIガイドです。サインインして会話を始めましょう。"
    },
    sidebar: {
      newChat: "新しい会話",
      today: "今日",
      yesterday: "昨日",
      last7Days: "過去7日間",
      older: "それ以前",
      noConversations: "会話がありません",
      searchPlaceholder: "会話を検索...",
      noResults: "会話が見つかりません",
      deleteConversation: "会話を削除しますか？",
      deleteConfirmation: "この会話とすべてのメッセージが完全に削除されます。",
      delete: "削除",
      renameConversation: "名前を変更",
      exportAll: "すべての会話をエクスポート"
    },
    export: {
      asJson: "JSONでエクスポート",
      asMarkdown: "Markdownでエクスポート",
      downloading: "ダウンロードを準備中...",
      success: "会話をエクスポートしました",
      singleSuccess: "会話をエクスポートしました",
      successDescription: "チャット履歴が正常にダウンロードされました",
      error: "エクスポートに失敗しました",
      errorDescription: "会話をエクスポートできませんでした。もう一度お試しください。",
      noConversations: "エクスポートする会話がありません"
    },
    copy: {
      tooltip: "クリップボードにコピー",
      success: "会話をコピーしました",
      successDescription: "Markdownをコピーしました。どこにでも貼り付けできます！",
      error: "コピーに失敗しました",
      errorDescription: "コピーできませんでした。もう一度お試しください。",
      noMessages: "コピーするメッセージがありません"
    },
    share: {
      button: "共有",
      dialogTitle: "会話を共有",
      dialogDescription: "このリンクを持つ誰でもこの会話を見ることができます。",
      createLink: "共有リンクを作成",
      generating: "リンクを生成中...",
      created: "リンクを作成しました",
      createdDescription: "会話を共有できるようになりました",
      linkCopied: "リンクをコピーしました",
      copyError: "リンクのコピーに失敗しました",
      revokeShare: "共有を取り消す",
      revoked: "共有を取り消しました",
      revokedDescription: "このリンクは機能しなくなります",
      revokeError: "取り消しに失敗しました",
      createError: "作成に失敗しました",
      createErrorDescription: "リンクを作成できませんでした。もう一度お試しください。",
      publicNote: "リンクを持つ誰でもこの会話を見ることができます",
      viewCount: "{{count}}回閲覧",
      views: "回閲覧",
      expiresAt: "{{date}}に有効期限切れ",
      expirationLabel: "リンクの有効期限",
      days7: "7日間",
      days30: "30日間",
      days90: "90日間"
    },
    sharedView: {
      title: "共有されたChatJCI会話",
      sharedBy: "JCIメンバーが共有",
      sharedOn: "{{date}}に共有",
      signUpCta: "あなた専用のAIアシスタントが欲しいですか？",
      signUpButton: "ChatJCIに登録",
      notFound: "会話が見つかりません",
      notFoundDescription: "このリンクは期限切れまたは取り消された可能性があります。",
      expired: "このリンクは期限切れです"
    },
    message: {
      copy: "コピー",
      copied: "コピーしました！",
      regenerate: "再生成",
      edit: "編集",
      stopGenerating: "停止",
      editPlaceholder: "メッセージを編集...",
      cancel: "キャンセル",
      saveAndSubmit: "保存して送信"
    },
    streaming: {
      generating: "生成中...",
      stopped: "停止しました"
    }
  },
  errorBoundary: {
    title: "問題が発生しました",
    description: "ご不便をおかけして申し訳ございません。予期しないエラーが発生しました。",
    tryAgain: "再試行",
    goHome: "ホームに戻る",
    errorDetails: "エラー詳細（開発環境のみ）",
    helpText: "この問題が続く場合は、ページを更新するか、サポートにお問い合わせください。",
    chunkLoadError: {
      title: "ページの読み込みに失敗しました",
      description: "ページを読み込めませんでした。ネットワークの問題または古いキャッシュが原因の可能性があります。",
      retry: "再試行",
      retryMessage: "試行 {{current}} / {{max}}",
      maxRetries: "最大再試行回数に達しました。"
    }
  },
  gdpr: {
    cookieTitle: "クッキー設定",
    cookieDescription: "サイトの使用状況を分析し、体験を向上させるためにクッキーを使用しています。お客様のデータは第三者に販売されることはありません。",
    learnMore: "プライバシーの取り組みについて詳しく",
    accept: "同意する",
    decline: "拒否する",
    analyticsTitle: "アナリティクスとクッキー",
    analyticsDescription: "体験向上のためのクッキーとアナリティクスの使用方法を管理",
    allowAnalytics: "アナリティクスを許可",
    allowAnalyticsDescription: "匿名の使用データを共有して改善にご協力ください",
    analyticsEnabled: "アナリティクスが有効になりました",
    analyticsEnabledDescription: "匿名の使用データでプラットフォームの改善にご協力いただけるようになりました。",
    analyticsDisabled: "アナリティクスが無効になりました",
    analyticsDisabledDescription: "使用データは収集されなくなります。",
    whatWeCollect: "収集する情報:",
    collectItem1: "ページビューとナビゲーションパターン",
    collectItem2: "機能のエンゲージメントとスクロール深度",
    collectItem3: "匿名のデバイスとブラウザ情報",
    neverCollect: "個人情報を収集したり、データを第三者に販売することはありません。"
  },
  apiDocs: {
    badge: "開発者API",
    hero: {
      title: "ユースフューチャーズ・オブザーバトリーAPI",
      subtitle: "114カ国の包括的な青少年開発データにアクセス。アプリケーションを構築し、YFIメトリクスをプロジェクトに統合しましょう。",
      getAccess: "APIアクセスを取得",
      viewDocs: "OpenAPI仕様を表示"
    },
    stats: { countries: "カ国", statistics: "統計", endpoints: "エンドポイント" },
    features: {
      badge: "なぜ当社のAPIを使うのか",
      title: "開発者のために設計",
      subtitle: "当社のAPIは、包括的な青少年開発メトリクスへの信頼性の高いアクセスを提供します。",
      sampleRequest: "リクエスト例",
      restful: { title: "RESTful JSON API", description: "クリーンで予測可能なJSONレスポンスを返す標準的なRESTエンドポイント。" },
      filtering: { title: "包括的なフィルタリング", description: "地域、分類、スコア範囲などでデータをフィルタリング。", highlight: "15+フィルター" },
      pagination: { title: "ページネーションとソート", description: "組み込みのページネーションで大規模なデータセットを効率的に処理。", highlight: "オフセットベース" },
      auth: { title: "安全なAPIキー", description: "キーごとのレート制限付きSHA-256ハッシュキー。" },
      logging: { title: "リクエストログ", description: "API使用状況の完全なアナリティクス。", highlight: "アナリティクス" },
      cors: { title: "CORS対応", description: "ブラウザベースのアプリ向けにクロスオリジンリクエストをネイティブサポート。" }
    },
    auth: {
      badge: "セキュリティ",
      title: "認証",
      subtitle: "すべてのAPIリクエストにはAPIキーによる認証が必要です。",
      step1: { title: "APIキーを取得", description: "設定に移動して新しいAPIキーを生成します。" },
      step2: { title: "ヘッダーに追加", description: "X-API-KeyヘッダーにAPIキーを含めます。" },
      step3: { title: "構築を開始", description: "準備完了！任意のエンドポイントにリクエストを送信できます。" },
      headerExample: "ヘッダー形式"
    },
    endpoints: {
      badge: "APIリファレンス",
      title: "利用可能なエンドポイント",
      subtitle: "すべてのYFIデータにアクセスするための5つの強力なエンドポイント。",
      parameters: "クエリパラメータ",
      sampleRequest: "リクエスト例",
      sampleResponse: "レスポンス例",
      yfi: { title: "YFIランキング", description: "すべての国のYouth Futures Indexスコアとランキングにアクセス。" },
      countries: { title: "国別プロファイル", description: "人口統計、JCIプレゼンスを含む詳細な国別データ。" },
      stats: { title: "グローバル統計", description: "メンバー数を含むJCI集計統計。" },
      indicators: { title: "指標メタデータ", description: "すべてのYFI指標の詳細な定義、方法論、しきい値を取得。認証不要。" },
      benchmarks: { title: "地域ベンチマーク", description: "平均、中央値、パーセンタイル、トップパフォーマンス国を含む地域別統計ベンチマーク。" }
    },
    params: {
      yfi: { region: "JCI地域でフィルタリング", classification: "YFIレベルでフィルタリング", code: "ISOコードで特定の国を取得", sort: "ソートフィールド", order: "ソート方向: asc または desc", limit: "ページあたりの結果数", offset: "ページネーションオフセット", countryCodes: "カンマ区切りのISOコードで複数国をフィルタリング", fields: "特定のフィールドのみを返す" },
      countries: { slug: "国プロファイル識別子" },
      stats: { key: "特定の統計を取得", category: "カテゴリでフィルタリング" },
      indicators: { key: "特定の指標キーでフィルタリング", category: "指標カテゴリでフィルタリング", lang: "翻訳された名前/説明の言語コード" },
      benchmarks: { region: "地域ベンチマークの地域コード", indicator: "ベンチマーク用の特定指標", top: "含めるトップパフォーマンス国数" }
    },
    table: { parameter: "パラメータ", type: "型", description: "説明" },
    responseFormat: {
      title: "レスポンス形式",
      subtitle: "すべてのエンドポイントは一貫したJSON構造を返します。",
      structure: "標準レスポンス構造",
      success: { title: "データ配列", description: "結果は'data'フィールドに返されます。" },
      meta: { title: "メタデータ含む", description: "各レスポンスにはページネーション情報とリクエストIDが含まれます。" }
    },
    errors: {
      title: "エラー処理",
      subtitle: "APIは標準のHTTPステータスコードを使用します。",
      "401": { title: "未認証", description: "APIキーが欠落、無効、または取り消されています。" },
      "404": { title: "見つかりません", description: "リクエストされたリソースは存在しません。" },
      "405": { title: "メソッド不許可", description: "GETリクエストのみがサポートされています。" },
      "429": { title: "レート制限超過", description: "レート制限を超過しました。" },
      "500": { title: "サーバーエラー", description: "予期しないエラーが発生しました。" }
    },
    useCases: {
      title: "ユースケース",
      subtitle: "組織がYFIデータをどのように活用しているかをご覧ください。",
      research: { title: "学術研究", description: "大学がYFIデータを比較研究に使用。" },
      journalism: { title: "データジャーナリズム", description: "報道機関がインパクトのある可視化を作成。" },
      ngo: { title: "NGO計画", description: "非営利団体が地域的洞察を活用して計画。" },
      policy: { title: "政策分析", description: "政府機関がYFIメトリクスを分析。" }
    },
    quickStart: { title: "クイックスタート", subtitle: "数秒で最初のAPIリクエストを実行。cURLコマンドをコピーしてAPIキーを置き換えてください。" },
    copy: "コピー",
    copied: "コピーしました！",
    codeCopied: "コードをクリップボードにコピーしました",
    copyFailed: "コードのコピーに失敗しました",
    cta: { title: "構築の準備はできましたか？", subtitle: "APIキーを生成して、今日からYFIデータで構築を始めましょう。クレジットカード不要。", button: "APIキーを取得" }
  }
} as const;

